import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Space,
  Spin,
  Typography
} from 'antd';
import {
  AudioMutedOutlined,
  AudioOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import { createParser } from 'eventsource-parser';

const { Title, Text } = Typography;

const API_URLS = {
  staging: 'https://api.vectara.dev/v2/query',
  production: 'https://api.vectara.io/v2/query'
};

const DEFAULT_SETTINGS = {
  environment: 'staging',
  preset: 'vectara-summary-ext-24-05-med-omni',
  corpusKey: '1234567',  // Default corpus key
  speed: 1.0,
  apiKey: 'your-api-key-here'  // Default API key
};

const AudioCommunicationApp = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const mediaRecorder = useRef(null);
  const audioContext = useRef(null);
  const audioChunks = useRef([]);
  const audioPlayer = useRef(new Audio());

  const [form] = Form.useForm();
  const [summary, setSummary] = useState('');

  // Initialize form with default values
  useEffect(() => {
    form.setFieldsValue(DEFAULT_SETTINGS);
  }, [form]);

  useEffect(() => {
    audioPlayer.current.onended = () => setIsPlaying(false);
    audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, []);

  const startRecording = async () => {
    setSummary('');
    setError('');
    setAudioUrl('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
        const audioBase64 = await blobToBase64(audioBlob);
        await sendAudioToAPI(audioBase64);
      };

      mediaRecorder.current.start();
      setIsRecording(true);
    } catch (err) {
      setError('Error accessing microphone: ' + err.message);
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const stopRecording = () => {
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
      setIsRecording(false);
    }
  };

  const sendAudioToAPI = async (base64Audio) => {
    setIsLoading(true);
    setError('');
    try {
      const values = form.getFieldsValue();
      let apiData = JSON.stringify({
        query: "",
        audio_query: {
          audio: base64Audio,
          mime_type: "audio/wav",
          filename: "audio.wav"
        },
        search: {
          corpora: [{ corpus_key: values.corpusKey }],
          offset: 0,
          limit: 20,
          reranker: { type: "none" }
        },
        stream_response: true,
        generation: {
          generation_preset_name: values.preset,
          enable_factual_consistency_score: false,
          audio: { speed: values.speed }
        }
      });

      let summary = '';
      let audio = '';
      await streamPost(values, apiData,
        (chunk) => {
          if (chunk.type === 'generation_chunk' && chunk['generation_chunk']) {
            summary += chunk['generation_chunk'];
            setSummary(summary);
          }

          if (chunk.type === 'audio_response') {
            audio = chunk['audio_response'] || chunk['audio'];
          }
        },
        () => console.log('Stream complete'),
        console.error
      );

      setAudioUrl(`data:audio/mp3;base64,${audio}`);
    } catch (err) {
      setError('Error sending audio to API: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const playAudio = useCallback(() => {
    if (audioUrl) {
      audioPlayer.current.src = audioUrl;
      audioPlayer.current.play().catch(console.error);
      setIsPlaying(true);
    }
  }, [audioUrl]);

  useEffect(() => {
    if (audioUrl) {
      playAudio();
    }
  }, [audioUrl, playAudio]);

  return (
    <Card style={{ maxWidth: 500, margin: '0 auto', marginTop: 20 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={2}>Vectara Audio Demo</Title>

        <Form 
          form={form} 
          layout="vertical" 
        >
          <Form.Item label="Environment" name="environment" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="staging">Staging</Radio>
              <Radio value="production">Production</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item 
            name="corpusKey" 
            label="Corpus Key" 
            rules={[{ required: true, message: 'Please enter corpus key' }]}
          >
            <Input placeholder="Enter corpus key" />
          </Form.Item>
          <Form.Item 
            name="preset" 
            label="Preset" 
            rules={[{ required: true, message: 'Please enter preset' }]}
          >
            <Input placeholder="Enter preset name" />
          </Form.Item>
          <Form.Item 
            name="speed" 
            label="Speed" 
            rules={[{ required: true, message: 'Please enter speed' }]}
          >
            <InputNumber min={0.5} max={2} step={0.1} />
          </Form.Item>
          <Form.Item 
            name="apiKey" 
            label="API Key" 
            rules={[{ required: true, message: 'Please enter API key' }]}
          >
            <Input.Password placeholder="Enter API key" />
          </Form.Item>
        </Form>

        <Row gutter={16}>
          <Col span={12}>
            <Button
              type={isRecording ? "danger" : "primary"}
              icon={isRecording ? <AudioMutedOutlined /> : <AudioOutlined />}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isLoading}
              block
            >
              {isRecording ? 'Stop Recording' : 'Start Recording'}
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={playAudio}
              disabled={!audioUrl || isPlaying || isLoading}
              block
            >
              {isPlaying ? 'Playing...' : 'Play Response'}
            </Button>
          </Col>
        </Row>

        {isLoading && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Spin size="large" />
            <Text style={{ display: 'block', marginTop: 10 }}>Processing audio...</Text>
          </div>
        )}

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError('')}
          />
        )}

        {audioUrl && !isLoading && (
          <Alert
            message="Success"
            description="Audio response received and ready to play!"
            type="success"
            showIcon
          />
        )}

        {summary && (
          <div style={{ marginTop: 16 }}>
            <Title level={4}>Summary</Title>
            <Text>{summary}</Text>
          </div>
        )}
      </Space>
    </Card>
  );
};

const streamPost = (values, apiData, onChunk, onComplete, onError) => {
  return new Promise((resolve, reject) => {
    const url = API_URLS[values.environment];
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': values.apiKey,
      },
      body: apiData,
    };

    fetch(url, options)
      .then(response => {
        if (!response.ok) {
          reject(new Error(`HTTP error! status: ${response.status}`));
          return;
        }

        const reader = response.body.getReader();
        const parser = createParser((event) => {
          if (event.type === 'event') {
            onChunk(JSON.parse(event.data));
          }
        });

        const decoder = new TextDecoder();

        function readStream() {
          reader.read().then(({ done, value }) => {
            if (done) {
              onComplete();
              resolve();
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            parser.feed(chunk);
            readStream();
          }).catch(error => {
            onError(error);
          });
        }

        readStream();
      })
      .catch(error => {
        onError(error);
      });
  });
};

export default AudioCommunicationApp;